import { SchedulerItem, SchedulerItemContent } from '@progress/kendo-react-scheduler';
import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { COLUMNSTYPE, LOCALSTORAGE_KEY, MEDIACATEGORIES, MODULE, TX_MODE, TX_STATUS } from '../../framework/constant/constant';
import { utility } from '../../framework/utility/utilityProvider';
import { PlanningHelper } from './helper/PlanningHelper';
import moment from 'moment';

// Status Icons Component - extracted for cleaner code
const StatusIcons = ({
  isPlanningItemLocked, isImportant, showFormat, showNoSegment,
  showContract, showRecursive, showInvalidFormat, isInvalidSegmentSelected,
  isChanged, txStatus, styleColor, hasAnyIcon, backgroundColor, isCatchUp
}) => {
  if (!hasAnyIcon) return null;

  return (
    <div style={{
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      fontSize: '10px',
      padding: '3px 6px',
      backgroundColor: backgroundColor ?? '#f8f9fa',
      borderTop: '1px solid rgba(0,0,0,0.1)',
      zIndex: 2,
      pointerEvents: 'none',
      borderRadius: '0 0 4px 4px'
    }}>
      {/* Left side icons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {isPlanningItemLocked && (
          <i className="fa fa-lock" style={{ fontSize: '12px', color: '#dc3545' }} title="Locked" />
        )}
      </div>

      {/* Right side icons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {isImportant && (
          <i className="fa fa-exclamation-triangle" style={{ fontSize: '11px', color: '#ffc107' }} title="Important" />
        )}
        {showFormat && (
          <i className="fa fa-cube" style={{ fontSize: '11px' }} title="Format" />
        )}
        {showNoSegment && (
          <i className="fa fa-window-close" style={{ fontSize: '11px', color: '#dc3545' }} title="No segments" />
        )}
        {showContract && (
          <i className="fa fa-pencil-square-o" style={{ fontSize: '11px' }} title="Contract" />
        )}
        {showRecursive && (
          <i className="fa fa-bars" style={{ fontSize: '11px' }} title="Recursive" />
        )}
        {showInvalidFormat && isInvalidSegmentSelected && (
          <i className="fa fa-triangle-exclamation" style={{ fontSize: '11px', color: '#dc3545' }} title="Invalid format" />
        )}
        {isChanged && (
          <i className="fa fa-refresh" style={{ fontSize: '11px' }} title="Changed" />
        )}
        {txStatus.SID === TX_STATUS.Live && (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '2px',
            padding: '1.5px 2px 0px',
            backgroundColor: '#dc3545',
            color: 'white',
            fontSize: '8px',
          }} title="Live">
            <i className="fa fa-circle" style={{ fontSize: '6px' }} /> LIVE
          </span>
        )}
        {txStatus.SID === TX_STATUS.Repeat && (
          <span style={{
            padding: '1.5px 2px 0px',
            backgroundColor: '#6c757d',
            color: 'white',
            fontSize: '8px',
          }} title="Repeat">R</span>
        )}
        {txStatus.SID === TX_STATUS.SDD && (
          <span style={{
            padding: '1.5px 2px 0px',
            backgroundColor: '#fd7e14',
            color: 'white',
            fontSize: '8px',
          }} title="Delayed">SDD</span>
        )}
        {txStatus.SID === TX_STATUS.Transmission && (
          <span style={{
            padding: '1.5px 2px 0px',
            backgroundColor: '#17a2b8',
            color: 'white',
            fontSize: '8px',
          }} title="TX Mode">TX</span>
        )}
        {isCatchUp && (
          <i className="fa fa-clock-o" style={{ fontSize: '11px' }} title="Catchup" />
        )}
      </div>
    </div>
  );
};

// Main component - NO React.memo to ensure drag/drop works correctly
const CustomPlanningItemV2 = (props) => {
  const { onFocus } = props;

  // Tooltip state
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const hoverTimeoutRef = useRef(null);
  const itemRef = useRef(null);

  const columnPreference = PlanningHelper.getColumnPreference();
  const planningPreference = utility.getValue(LOCALSTORAGE_KEY.planningpreference);
  const showFormat = planningPreference?.data[0]?.value?.ShowFormat && props?.dataItem?.mediaEpisode?.Formats?.length > 0;
  const showNoSegment = planningPreference?.data[0]?.value?.ShowNoSeg && !props?.dataItem?.Segment_id;
  const showContract = planningPreference?.data[0]?.value?.ShowContract && props?.dataItem?.MediaDealRight_id;
  const showInvalidFormat = planningPreference?.data[0]?.value?.ShowInvalidFormat ?? false;

  const IsInvalidSegmentSelected = (planneditem) => {
    if (planneditem?.dataItem?.Formats?.length > 0 && planneditem?.dataItem?.segment?.SegmentType) {
      return !planneditem?.dataItem?.Formats?.some((x) => x?.SegmentType?.SegmentCount === planneditem?.dataItem?.segment?.SegmentType?.SegmentCount);
    }
    return false;
  }

  const isInvalidSegmentSelected = IsInvalidSegmentSelected(props);

  let showTxMode = false;
  let txModeValue = '';
  let txStatus = props.dataItem?.TXStatus ? props.dataItem?.TXStatus : { SID: TX_STATUS.Transmission, Description: 'Transmission' };
  const showRecursive = (props?.dataItem?.RecursionParentProgramSchedule_id != undefined && props?.dataItem?.RecursionParentProgramSchedule_id != "") ? true : false;
  const isChanged = props?.dataItem?.isChanged ?? false;
  const scheduleDate = new Date(props.dataItem?.ScheduleDate).setUTCHours(0, 0, 0, 0);
  const channel = props?.dataItem?.Channel;
  const userData = utility.getValue(LOCALSTORAGE_KEY.userData);
  const modify = utility.canModify(props.lockedPlaylist, MODULE.PLANNING, channel._id, scheduleDate);
  var canModify = modify.canModify;
  const lockedBy = modify.lockedBy;
  const isPlanningItemLocked = props.dataItem?.isLocked ?? false;
  const isImportant = props.dataItem?.isImportant ?? false;
  const isCatchUp = props.dataItem?.IsCatchUp ?? false;

  // Enhanced styling with modern look
  var style = {
    ...props.style,
    letterSpacing: '0.5px',
    fontWeight: '600',
    fontSize: '11px',
    ...props.dataItem.Style,
    border: props.selected ? '2px solid #212529' : 'none',
    borderRadius: '4px',
    boxShadow: props.selected ? '0 2px 8px rgba(0,0,0,0.15)' : '0 1px 3px rgba(0,0,0,0.08)',
    pointerEvents: canModify ? 'auto' : 'none',
    overflow: 'hidden'
  }

  // ALL ORIGINAL LOGIC FOR uiData PRESERVED
  let uiData = columnPreference.map((item, index) => {
    if (!props?.dataItem) {
      console.error("props.dataItem is undefined", props);
      return "";
    }

    let value = item?.key
      ? props?.dataItem[item?.key]?.[item?.name]
      : props?.dataItem?.[item?.name];

    if (item.name === "EpisodeNumber") {
      value = value
        ? ((props.dataItem?.mediaEpisode?.MediaCategory?.SID ?? 0) === MEDIACATEGORIES.Movie ? "NA" : "EP " + value)
        : "";
    }
    else if (item.name === "ExhibitionNumber") {
      value = "Exhibition: " + (typeof value === "number" ? value : value?.toString() ?? "");
    }
    else if (item.name === "RepeatNumber") {
      value = "Repeat: " + (typeof value === "number" ? value : value?.toString() ?? "");
    }
    else if (item.name === "SeasonNumber") {
      let temp = props.dataItem?.mediaEpisode?.SeasonNumber;
      value = temp
        ? ((props.dataItem?.mediaEpisode?.MediaCategory?.SID ?? 0) === MEDIACATEGORIES.Movie ? "NA" : "S-" + temp)
        : "";
    }
    else if (item.name === "NominalDuration") {
      let finalNominalDuration = props.dataItem.customDuration ?? props.dataItem.NominalDuration;
      value = utility.convertMilisecondsToStringWithFrames(finalNominalDuration);
    }
    else if (item.name === "TX Mode") {
      if (props.dataItem?.Segment_id !== null && (props.dataItem?.segments?.length > 0 || props.dataItem?.mediaEpisode?.segments?.length > 0)) {
        let currentSegment = props.dataItem?.segment ?? props.dataItem?.mediaEpisode?.segments.find((x) => x._id === props.dataItem?.Segment_id);
        let temp = currentSegment?.SegmentType?.TXMode
          ? currentSegment?.SegmentType?.TXMode?.Name
          : utility.getKeyByValue(TX_MODE, currentSegment?.SegmentType?.TXModeSID);
        showTxMode = !!temp;
        value = temp ?? "No Segments";
        txModeValue = value;
      } else {
        value = "No Segments";
        txModeValue = value;
      }
    } else if (item.name == "Break Details") {
      if (props.dataItem && props.dataItem.segment && props.dataItem.Formats && props.dataItem.Formats.length > 0) {
        let validFormat = props.dataItem.Format_id
          ? props.dataItem.Formats.find((x) => x._id === props.dataItem.Format_id)
          : props.dataItem.Formats.find((x) => {
            return x?.SegmentType?.SegmentCount === props.dataItem?.segment?.SegmentType?.SegmentCount &&
              x.Channel.SID === props.dataItem.Channel.SID
          })

        if (validFormat) {
          value = `${validFormat?.Name} - ${validFormat.TotalBreakCount} BREAKS`
        }
      }
    }
    else if (item.type === COLUMNSTYPE.array) {
      value = value?.length > 0 ? (typeof value[0]?.[item.format] === "number" ? value[0][item.format] : value[0]?.[item.format]?.toString() ?? "") : "";
    }
    else if (item.type === COLUMNSTYPE.time) {
      value = utility.convertMilisecondsToStringWithFrames(value ?? 0);
    }
    else if (item.type === COLUMNSTYPE.date) {
      value = utility.convertMilisecondsToStringWithFrames(value ?? 0);
    }
    else if (item.name === "Language") {
      value = props.dataItem?.segment?.Language?.Language?.Description ?? "";
    }

    return value && typeof value === "number" ? value : value?.toString() ?? "";
  });

  // Time range for tooltip header
  const startTime = moment(props?.dataItem?.SlotDateTime).utc().format('HH:mm A');
  const endTime = moment(props?.dataItem?.SlotDateTime + props?.dataItem?.ActualDuration).utc().format('HH:mm A');
  const title = props?.dataItem?.Title;

  const handleFocus = useCallback((event) => {
    if (onFocus) {
      props.setFocusedPlanningItemPropRef(props);
      onFocus(event);
    }
  }, [onFocus]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Check if any icon should show
  const hasAnyIcon = isPlanningItemLocked || isImportant || showFormat || showNoSegment ||
    showContract || showRecursive || isChanged ||
    (showInvalidFormat && isInvalidSegmentSelected) || txStatus.SID;

  // Custom tooltip content - styled like the planning item
  const renderTooltip = () => {
    if (!showTooltip) return null;

    return ReactDOM.createPortal(
      <div
        style={{
          position: 'fixed',
          left: tooltipPosition.x,
          top: tooltipPosition.y,
          maxWidth: '320px',
          maxHeight: '300px',
          backgroundColor: props?.dataItem?.Style?.backgroundColor || '#f8f9fa',
          color: props?.dataItem?.Style?.color || '#212529',
          borderRadius: '6px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
          border: '2px solid rgba(0,0,0,0.15)',
          zIndex: 999999,
          pointerEvents: 'none',
          overflow: 'hidden',
          fontFamily: 'inherit',
          opacity: 0.95
        }}
      >
        {/* Header with time and title */}
        <div style={{
          padding: '10px 12px',
          backgroundColor: 'rgba(0,0,0,0.1)',
          borderBottom: '1px solid rgba(0,0,0,0.1)'
        }}>
          <div style={{
            fontSize: '12px',
            fontWeight: 'bold',
            marginBottom: '4px',
            color: 'inherit'
          }}>
            {startTime} - {endTime}
          </div>
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: 'inherit',
            wordBreak: 'break-word'
          }}>
            {title}
          </div>
        </div>

        {/* Details */}
        <div style={{
          padding: '10px 12px',
          maxHeight: '200px',
          overflowY: 'auto'
        }}>
          {uiData.map((item, index) => {
            if (item) {
              return (
                <div
                  key={index}
                  style={{
                    fontSize: '11px',
                    padding: '3px 0',
                    borderBottom: index < uiData.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                    color: 'inherit'
                  }}
                >
                  {item}
                </div>
              );
            }
            return null;
          })}
        </div>

        {/* Status bar */}
        {hasAnyIcon && (
          <div style={{
            padding: '6px 12px',
            backgroundColor: 'rgba(0,0,0,0.05)',
            borderTop: '1px solid rgba(0,0,0,0.1)',
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
            alignItems: 'center',
            fontSize: '10px'
          }}>
            {isPlanningItemLocked && <span><i className="fa fa-lock" style={{ color: '#dc3545' }} /> Locked</span>}
            {isImportant && <span><i className="fa fa-exclamation-triangle" style={{ color: '#ffc107'}} /> Important</span>}
            {showFormat && <span><i className="fa fa-cube" /> Format</span>}
            {showNoSegment && <span><i className="fa fa-window-close" /> No Segments</span>}
            {showContract && <span><i className="fa fa-pencil-square-o" /> Contract</span>}
            {showRecursive && <span><i className="fa fa-bars" /> Recursive</span>}
            {isChanged && <span><i className="fa fa-refresh" /> Changed</span>}
            {txStatus.SID === TX_STATUS.Live && <span><i className="fa fa-circle" style={{ color: '#dc3545' }} /> LIVE</span>}
            {txStatus.SID === TX_STATUS.Repeat && <span>R - Repeat</span>}
            {txStatus.SID === TX_STATUS.SDD && <span>SDD - Delayed</span>}
            {txStatus.SID === TX_STATUS.Transmission && <span>TX - Transmission</span>}
            {isCatchUp && <span><i className="fa fa-clock-o"/> Catchup</span>}
          </div>
        )}
      </div>,
      document.body
    );
  };

  // Attach mouse event listeners to the actual DOM element
  useEffect(() => {
    const element = document.getElementById(props?.id);
    if (!element) return;

    const onMouseEnter = (e) => {
      // Clear any existing timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }

      // Get element position
      const rect = element.getBoundingClientRect();

      // Calculate tooltip position - prefer right side, fallback to left if near viewport edge
      let x = rect.right + 10;
      let y = rect.top;

      // If tooltip would overflow right side of viewport, show on left
      if (x + 320 > window.innerWidth) {
        x = rect.left - 330;
      }
      // If still overflows left, center it above
      if (x < 10) {
        x = Math.max(10, rect.left);
        y = rect.top - 200;
      }
      // Ensure tooltip doesn't go off bottom of viewport
      if (y + 300 > window.innerHeight) {
        y = window.innerHeight - 310;
      }
      // Ensure tooltip doesn't go off top
      if (y < 10) {
        y = 10;
      }

      setTooltipPosition({ x, y });

      // Show tooltip after 500ms delay (like native tooltips)
      hoverTimeoutRef.current = setTimeout(() => {
        setShowTooltip(true);
      }, 500);
    };

    const onMouseLeave = () => {
      // Clear timeout and hide tooltip
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      setShowTooltip(false);
    };

    // Also remove native title attribute from the element to prevent browser tooltip
    const innerDiv = element.querySelector('div');
    if (innerDiv) {
      innerDiv.removeAttribute('title');
    }

    element.addEventListener('mouseenter', onMouseEnter);
    element.addEventListener('mouseleave', onMouseLeave);

    return () => {
      element.removeEventListener('mouseenter', onMouseEnter);
      element.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [props?.id]);

  return (
    <>
      <SchedulerItem {...props}
        style={style}
        onFocus={handleFocus}
        onClick={(event) => {
          // Hide tooltip on click
          setShowTooltip(false);
          if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
          }

          if (event.syntheticEvent.detail == 2) {
            //dblclick
          } else {
            //singleClick
            props.onClick(event);
            props.onPlannerItemSelect(event, event.target.props.dataItem);
          }
        }}
      >
        {/* Children rendered without title (title removed via DOM in useEffect) */}
        {props.children}
        <SchedulerItemContent>
          <div style={{
            padding: '4px 6px',
            paddingBottom: hasAnyIcon ? '24px' : '4px',
            lineHeight: '1.3'
          }}>
            {uiData.map((item, index) => {
              if (item) {
                return (
                  <div
                    key={index + "-" + props.dataItem?._id}
                    className='customItem'
                    style={{
                      marginBottom: '2px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {item}
                  </div>
                );
              }
              return null;
            })}
          </div>
        </SchedulerItemContent>

        <StatusIcons
          isPlanningItemLocked={isPlanningItemLocked}
          isImportant={isImportant}
          showFormat={showFormat}
          showNoSegment={showNoSegment}
          showContract={showContract}
          showRecursive={showRecursive}
          showInvalidFormat={showInvalidFormat}
          isInvalidSegmentSelected={isInvalidSegmentSelected}
          isChanged={isChanged}
          txStatus={txStatus}
          styleColor={style?.color}
          hasAnyIcon={hasAnyIcon}
          backgroundColor={props?.dataItem?.Style?.backgroundColor}
          isCatchUp={isCatchUp}
        />
      </SchedulerItem>

      {/* Custom tooltip rendered via Portal */}
      {renderTooltip()}
    </>
  );
};

export default CustomPlanningItemV2;